import React, { useEffect, useMemo } from 'react';
import * as Linking from 'expo-linking';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { observer } from 'mobx-react-lite';
import { Platform } from 'react-native';

import {
  ForgotPasswordScreen,
  OnboardingScreen,
  SignInScreen,
  SignUpScreen,
  SetNewPasswordScreen,
  ChooseSubscriptionPlanScreen,
  AddPaymentMethodScreen,
  ReferralScreen,
} from '@screens';
import { screens } from './screens';
import type { AuthStackParamListType } from './types';
import { getIdFromPath, IS_WEB } from '@utils';
import { WebHeader } from '@components';
import { useDeepLinking } from '@hooks';

const AuthStack =
  createNativeStackNavigator<AuthStackParamListType>();

type InitialRouteType = {
  name: keyof AuthStackParamListType;
  params?: object;
};

// On WEB we will not have preferredInitialUrl because preferredInitialUrl set
// by push notifications or deep linking services, which we are not using on WEB.
// Deep linking service value (redirect to WEB app) on WEB will be reachable
// via window.location.href
const initialUrl = IS_WEB ? window.location.href : null;

export const AuthNavigator = observer(() => {
  const deepLinking = useDeepLinking();

  useEffect(() => {
    if (!IS_WEB || deepLinking.hasEverBeenAuthorized) {
      // Do nothing
      return;
    }
    // For WEB store init url to DeepLinking context
    deepLinking.setInitialUrl(initialUrl);
  }, []);

  function getParsedUrl(): Linking.ParsedURL | null {
    if (deepLinking.hasEverBeenAuthorized) {
      return null;
    }

    const url: string | null = Platform.select({
      web: initialUrl,
      default: deepLinking.preferredInitialUrl,
    });

    if (!url) {
      return null;
    }

    return Linking.parse(url);
  }

  const initRoute: InitialRouteType = useMemo(() => {
    const parsedUrl = getParsedUrl();

    if (parsedUrl) {
      const { path, queryParams } = parsedUrl;

      if (queryParams?.invite) {
        const inviteVideoId = path?.includes('tutorial')
          ? getIdFromPath(path)
          : undefined;

        return {
          name: Platform.select({
            default: screens.SignUp,
            web: screens.Referral,
          }),
          params: {
            invite: queryParams.invite,
            inviteVideoId,
          },
        };
      }
    }

    return Platform.select({
      web: { name: screens.SignIn },
      default: { name: screens.Onboarding },
    });
  }, []);

  return (
    <AuthStack.Navigator
      initialRouteName={initRoute.name}
      screenOptions={{ headerShown: false }}
    >
      <AuthStack.Screen
        component={OnboardingScreen}
        name={screens.Onboarding}
      />
      <AuthStack.Screen
        component={SignInScreen}
        name={screens.SignIn}
      />
      <AuthStack.Screen
        component={SignUpScreen}
        name={screens.SignUp}
        initialParams={initRoute?.params}
      />
      <AuthStack.Screen
        component={ForgotPasswordScreen}
        name={screens.ForgotPassword}
      />
      <AuthStack.Screen
        component={SetNewPasswordScreen}
        name={screens.SetNewPassword}
      />
      <AuthStack.Screen
        component={AddPaymentMethodScreen}
        name={screens.AddPaymentMethod}
      />
      <AuthStack.Screen
        component={ChooseSubscriptionPlanScreen}
        name={screens.ChooseSubscriptionPlan}
        options={{
          headerShown: IS_WEB,
          header: () => <WebHeader isShowTabButtons={false} />,
        }}
      />
      <AuthStack.Screen
        component={ReferralScreen}
        name={screens.Referral}
        initialParams={initRoute.params}
        options={{
          headerShown: IS_WEB,
          header: () => <WebHeader isShowTabButtons={false} />,
        }}
      />
    </AuthStack.Navigator>
  );
});
